//Libs
import XMarkIcon from "@heroicons/react/24/solid/XMarkIcon";
import { useState, useEffect } from "react";
import SendIcon from "@mui/icons-material/Send";

//Local
import * as strings from "../../strings";
import BaseModal from "../BaseModal";
import BaseButtonPrimary from "../BaseButtonPrimary";
import { ErrorMessage } from "../ErrorMessage";
import BaseInputTextArea from "../BaseInputTextArea";
import MultipleEmailSelector from "../MultipleEmailSelector";
import BaseButtonSecondary from "../BaseButtonSecondary";
import HeadingOne from "../HeadingOne";
import { POStatusValues, OPOStatus } from "../../models/purchase-order";

interface Props {
  /* DATA */
  isDialogOpen: boolean;
  customerEmailList: string[];
  purchaseOrderNumber: string;
  defaultEmailBody?: string;
  purchaseOrderStatus: POStatusValues;
  /* FUNCTIONS */
  closeDialog: () => void;
  sendPurchaseOrderEmail: (
    emailList: string[],
    emailBody: string,
  ) => Promise<void>;
}

export default function SendPurchaseOrderEmailDialog(props: Props) {
  const [selectedEmailList, setSelectedEmailList] = useState<string[]>([]);
  const [errorString, setErrorString] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [emailBody, setEmailBody] = useState<string>(
    props.defaultEmailBody ||
      `Please find attached Purchase Order #${props.purchaseOrderNumber} for your review.\n\nThank you for your business.`,
  );

  useEffect(() => {
    if (props.defaultEmailBody) {
      setEmailBody(props.defaultEmailBody);
    }
  }, [props.defaultEmailBody]);

  function closeAndReset() {
    setSelectedEmailList([]);
    setErrorString(null);
    setEmailBody(
      props.defaultEmailBody ||
        `Please find attached Purchase Order #${props.purchaseOrderNumber} for your review.\n\nThank you for your business.`,
    );
    props.closeDialog();
  }

  const shouldShowStatusWarning =
    props.purchaseOrderStatus === OPOStatus.DRAFT ||
    props.purchaseOrderStatus === OPOStatus.PENDING;

  const dialogHeader = (
    <div className="flex w-full items-center justify-between rounded-t-lg bg-primary p-8 text-left text-white">
      <HeadingOne fontColor="text-white" fontSize="text-xl">
        Email Purchase Order #{props.purchaseOrderNumber}
      </HeadingOne>
      <button type="button" onClick={closeAndReset}>
        <XMarkIcon aria-label="close email dialog" className="h-6 text-white" />
      </button>
    </div>
  );

  const displayError = errorString && (
    <span className="mx-auto mt-4">
      <ErrorMessage
        message={errorString}
        clearMessage={() => setErrorString(null)}
      />
    </span>
  );

  const sendEmailButton = (
    <BaseButtonPrimary
      type="button"
      busyText={strings.buttons.BUSY_SENDING}
      isBusy={isBusy}
      onClick={async () => {
        if (selectedEmailList.length === 0) {
          setErrorString("Please select at least one email address");
          return;
        }

        if (emailBody.trim().length === 0) {
          setErrorString("Please enter an email message");
          return;
        }

        setIsBusy(true);
        try {
          await props.sendPurchaseOrderEmail(selectedEmailList, emailBody);
          setIsBusy(false);
          setSelectedEmailList([]);
          closeAndReset();
        } catch (error) {
          setIsBusy(false);
          setErrorString("Failed to send email. Please try again.");
        }
      }}
    >
      <SendIcon className="h-full" />
    </BaseButtonPrimary>
  );

  return (
    <BaseModal
      closeModal={closeAndReset}
      open={props.isDialogOpen}
      title={dialogHeader}
      parentDivStyles="inline-block transform max-w-screen-sm rounded-lg bg-white text-left align-middle shadow-xl transition-all overflow-visible"
    >
      <div className="relative flex flex-col justify-center space-y-4 p-8 text-lg sm:gap-8 sm:space-y-0">
        {/* Status Warning */}
        {shouldShowStatusWarning && (
          <div className="rounded-md border border-blue-200 bg-blue-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-blue-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  Status Change Notice
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>
                    Sending this purchase order will automatically change its
                    status from{" "}
                    <span className="font-semibold">
                      {props.purchaseOrderStatus === OPOStatus.DRAFT
                        ? "Draft"
                        : "Pending"}
                    </span>{" "}
                    to <span className="font-semibold">Sent</span>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Fixed Subject Display */}
        <div className="rounded-md bg-gray-50 p-4">
          <label className="mb-2 block text-sm font-medium text-gray-700">
            Subject (Fixed)
          </label>
          <div className="text-sm font-medium text-gray-900">
            Purchase Order #{props.purchaseOrderNumber}
          </div>
        </div>

        {/* Email Recipients */}
        <MultipleEmailSelector
          sendEmailButton={sendEmailButton}
          selectedEmailList={selectedEmailList}
          setSelectedEmailList={setSelectedEmailList}
          emailList={props.customerEmailList}
          setErrorString={setErrorString}
          includeJobPhotosSwitch={null}
        />

        {/* Email Body */}
        <div>
          <BaseInputTextArea
            text="Email Message"
            admin={true}
            required={true}
            value={emailBody}
            onChange={(event) => setEmailBody(event.target.value)}
            className="w-full"
            rows={6}
            placeholder="Enter your message here..."
          />
        </div>

        <BaseButtonSecondary
          type="button"
          onClick={closeAndReset}
          className="w-full"
        >
          CLOSE WITHOUT SENDING
        </BaseButtonSecondary>

        {displayError}
      </div>
    </BaseModal>
  );
}
